/**
 * The event spine. Every other analytics module is keyed by this enum:
 * `properties.ts` (payload shapes), `registry.ts` (metadata), `funnels.ts` (funnel steps).
 *
 * Adding a member here is a compile error until it also has a payload type and a registry
 * entry — that is the whole point. Do not add an event without both.
 *
 * Historical sheet (pre-contract, kept for archaeology only):
 * https://docs.google.com/spreadsheets/d/1ArCow8_72ZfKrF7ADO_7Dv-zruOFhkKx1IhEpsIMuXg/edit?gid=0#gid=0
 * The generated `brain/vidmax/reference/analytics-contract.md` supersedes it.
 */
export enum EVMAnalyticsEvent {
  // Auth / Onboarding
  SIGN_UP = "SIGN_UP",
  SIGN_IN = "SIGN_IN",

  // Subscription
  SUBSCRIPTION_STARTED = "SUBSCRIPTION_STARTED",
  SUBSCRIPTION_CANCELLED = "SUBSCRIPTION_CANCELLED",
  SUBSCRIPTION_RENEWAL = "SUBSCRIPTION_RENEWAL",

  // Billing
  FAILED_PAYMENT = "FAILED_PAYMENT",
  PURCHASE = "PURCHASE",

  // Credits
  ADD_CREDITS = "ADD_CREDITS",
  CONSUME_CREDITS = "CONSUME_CREDITS",

  // Funnel / Conversion
  // Top-of-funnel landing view. Fires eagerly on the ad-landing route BEFORE signup so
  // an ordered funnel has a real anonymous step-1 (VM-958). $pageview can't be used —
  // backend SIGN_UP timestamps before the first deferred-init frontend $pageview lands.
  // PostHog auto-captures utm_*/fbclid from the URL onto this event; filter to ads with those.
  LANDING_VIEWED = "LANDING_VIEWED",
  PRICING_PAGE_VIEWED = "PRICING_PAGE_VIEWED",
  CHECKOUT_STARTED = "CHECKOUT_STARTED",
  CTA_CLICKED = "CTA_CLICKED",

  // UX / Engagement
  CLICK_COMING_SOON_FEATURE = "CLICK_COMING_SOON_FEATURE",
  PRO_FEATURE_UPSELL = "PRO_FEATURE_UPSELL",
  NOT_ENOUGH_CREDITS_UPSELL = "NOT_ENOUGH_CREDITS_UPSELL",

  // Content Creation & Editing
  CREATE_AUTOMATED_SERIES = "CREATE_AUTOMATED_SERIES",

  TEMPLATE_SELECTED = "TEMPLATE_SELECTED",
  TOOL_SELECTED = "TOOL_SELECTED",
  STORYBOARD_CREATED = "STORYBOARD_CREATED",
  EDITOR_OPENED = "EDITOR_OPENED",
  EDITOR_ASSET_MODIFIED = "EDITOR_ASSET_MODIFIED",
  VIDEO_RENDER_REQUESTED = "VIDEO_RENDER_REQUESTED",
  VIDEO_RENDER_SUCCEEDED = "VIDEO_RENDER_SUCCEEDED",
  VIDEO_RENDER_FAILED = "VIDEO_RENDER_FAILED",

  // ── Asset creation lifecycle (canonical) ───────────────────────────────────
  // One lifecycle == ONE top-level user request, NOT one AssetJob. A request that
  // fans out to 20 child jobs emits exactly 1 STARTED and exactly 1 terminal event;
  // the 20 job outcomes stay in the operational `vm_AssetJob` rows and never become
  // product analytics. Correlate the pair with `creation_id`.
  //
  // Transformations (upscale, background removal, instruction edit, lipsync…) belong
  // to this family too — they produce a new asset row and consume credits, so
  // "ASSET_CREATE" is literally accurate. Distinguish them via `transform_type`,
  // not via a separate event; see propertyEnums.ts for why.
  ASSET_CREATE_STARTED = "ASSET_CREATE_STARTED",
  ASSET_CREATE_COMPLETED = "ASSET_CREATE_COMPLETED",
  ASSET_CREATE_FAILED = "ASSET_CREATE_FAILED",

  // Distribution & Sharing
  POST_VIDEO = "POST_VIDEO",
  VIDEO_DOWNLOADED = "VIDEO_DOWNLOADED",

  // Generic tool usage
  TOOL_USE = "TOOL_USE",

  // Debug / Testing
  DEBUG_TEST = "DEBUG_TEST",

  // AI Script Writer (Bet #117)
  SCRIPT_IDEAS_REQUESTED = "script_ideas_requested",
  SCRIPT_IDEA_SELECTED = "script_idea_selected",
  SCRIPT_IDEA_REGENERATED = "script_idea_regenerated",

  // Generation Concurrency funnel (VM-880). queue-hit (server, enforce-only) → upsell-view
  // (client, CTA renders) → upgrade-click (client, CTA clicked). Distinct from C1's shadow
  // `concurrency-would-queue` log metric: that sizes the bet pre-enforce; these drive the funnel.
  GENERATION_QUEUE_HIT = "generation-queue-hit",
  GENERATION_UPSELL_VIEW = "generation-upsell-view",
  GENERATION_UPGRADE_CLICK = "generation-upgrade-click",

  // ── Deprecated: superseded by the ASSET_CREATE_* family ────────────────────
  // Still enumerated (and still typed) so historical dashboards and any not-yet-migrated
  // fire site keep compiling. The registry marks them DEPRECATED with a `replacedBy`,
  // and `analytics-audit` errors when the fire-site count exceeds
  // VM_LEGACY_CREATION_FIRE_SITE_BUDGET, so a NEW call site fails CI. Not for new code.
  /** @deprecated Use {@link EVMAnalyticsEvent.ASSET_CREATE_STARTED}. */
  VIDEO_CREATE_STARTED = "VIDEO_CREATE_STARTED",
  /** @deprecated Use {@link EVMAnalyticsEvent.ASSET_CREATE_COMPLETED}. */
  VIDEO_ASSETS_GENERATED = "VIDEO_ASSETS_GENERATED",
  /** @deprecated Use ASSET_CREATE_STARTED with `generation_action: "regenerate"`. */
  VIDEO_REGENERATED = "VIDEO_REGENERATED",
}

/**
 * Bumped whenever the shape of the canonical contract changes in a way that makes
 * old and new events non-comparable. Injected onto every event by the trackers, so a
 * dashboard can always tell which contract emitted a given row — this is what makes a
 * cutover auditable instead of a guess.
 */
export const VM_ANALYTICS_CONTRACT_VERSION = 2

/**
 * ISO date the version above was last bumped.
 *
 * Exists to make the bump mechanical rather than remembered. Retiring an event is a
 * breaking change to the contract, so `analytics-audit` fails when any event's
 * `retiredAt` is later than this date — you cannot retire an event and forget to bump
 * the version. Without this pair, `VM_ANALYTICS_CONTRACT_VERSION` silently rots into a
 * constant that never changes, and then it tells a cutover nothing.
 */
export const VM_ANALYTICS_CONTRACT_VERSION_BUMPED_AT = "2026-07-31"

/**
 * How many fire sites the legacy creation events are still allowed to have.
 *
 * `analytics-audit` errors when the real count EXCEEDS this, so the migration can only
 * ever shrink — adding a new `VIDEO_CREATE_STARTED` call fails CI instead of quietly
 * adding to the backlog. Lower it as sites are migrated; never raise it.
 *
 * Reporting deprecation as a warning alone was not enough: a warning is exactly what a
 * new fire site would produce, indistinguishable from the eight already there.
 */
export const VM_LEGACY_CREATION_FIRE_SITE_BUDGET = 8

/**
 * Events the ASSET_CREATE_* family replaced. `analytics-audit` uses this to detect
 * legacy leakage after cutover and to keep the generated doc's alias table honest.
 */
export const VM_LEGACY_CREATION_EVENTS: readonly EVMAnalyticsEvent[] = [
  EVMAnalyticsEvent.VIDEO_CREATE_STARTED,
  EVMAnalyticsEvent.VIDEO_ASSETS_GENERATED,
  EVMAnalyticsEvent.VIDEO_REGENERATED,
]
